<template>
  <!-- CS ES6 destructuring to a different name because we already have "pending" key on the child provider. -->
  <ValidationObserver
    ref="tagObserver"
    v-slot="{ valid, pristine, pending: pendingObserver }" >
    <b-modal
      id="tag-modal"
      ref="tagModal"
      modal-class="settings-modal"
      centered
      hide-header-close
      :title="modalTitle"
      size="sm"
      @hidden="resetFields"
      @shown="focus" >
      <div>
        <ValidationProvider
          v-slot="{ errors, dirty, pending }"
          name="name"
          :debounce="300"
          :rules="{
            required: true,
            noDuplicateRemote: {
              endpoint: `organisation/${organisationId}/contact-tags`,
              message: 'Tag already exists.'
            }
          }"
          mode="aggressive" >
          <b-form-group>
            <label
              for="p-key"
              class="settings__label mt-0" >
              Name
            </label>
            <div class="relative">
              <input
                ref="nameInputRef"
                v-model="tagName"
                type="text"
                class="w-100 sb2-bordered sb2-hoverable sb2-focusable"
                placeholder="Enter tag name" >
              <Spinner
                v-if="pending"
                :size="16"
                :thickness="2"
                class="small checking-input" />
            </div>
            <div
              v-if="errors[0] && dirty && !pending"
              class="error flex flex-align-center">
              <SBIcon
                class="mr-2"
                :icon="SBIconList.ERROR"
                :color="SBColors.redPrimary"
                :size="12" />
              <span>{{ errors[0] }}</span>
            </div>
          </b-form-group>
        </ValidationProvider>

        <div
          v-if="validationErrors"
          class="error-message fs-14 mt-3 flex flex-align-center px-0" >
          <SBIcon
            class="mr-2"
            :icon="SBIconList.ERROR"
            :color="SBColors.redPrimary"
            :size="12" />
          {{ validationErrors.name }}
        </div>
      </div>

      <template slot="modal-footer">
        <b-button
          :disabled="saving"
          @click="hide">
          Cancel
        </b-button>
        <b-button
          variant="primary"
          :disabled="saving || !valid || pristine || pendingObserver"
          :class="{loading: saving}"
          class="flex settings__actions--save"
          @click="commitUpdate">
          <span>Save</span>
          <Spinner
            v-if="saving"
            :size="14"
            :thickness="2"
            :color="SBColors.white" />
        </b-button>
      </template>
    </b-modal>
  </ValidationObserver>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import ToasterMixin from '@/mixins/Toaster';
import SBIconList from '@/helpers/lists/sb-icon-list';
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import Spinner from '@/components/common/Spinner';
import setValidationErrors from '@/helpers/validationErrors';

export default {
  name: 'CreateTagModal',
  components: {
    SBIcon,
    Spinner,
  },
  mixins: [
    ToasterMixin,
  ],
  props: {
    businessId: {
      type: String,
      required: true,
      default: '',
    },
  },
  data() {
    return {
      url: '',
      saving: false,
      validationErrors: null,
      update: false,
      tagName: '',
      initialEditName: '',
      tagId: null,
      modalTitle: null,
      SBIconList,
      SBColors,
    };
  },
  computed: {
    ...mapGetters({
      tagsList: 'contactTag/list',
      apiError: 'contactTag/apiError',
    }),
    organisationId() {
      return this.$route.params.organisationId;
    },
  },
  watch: {
    apiError(error) {
      this.saving = false;
      if (error.status === 422) {
        this.validationErrors = setValidationErrors(error.data.errors);
      } else {
        this.errorMessage = error.data.message;
      }
    },
  },
  methods: {
    ...mapActions({
      createContactTag: 'contactTag/createContactTag',
      updateContactTag: 'contactTag/updateContactTag',
      fetchOrganisationContactTags: 'contactTag/fetchOrganisationContactTags',
    }),
    resetFields() {
      this.update = false;
      this.tagName = '';
      this.tagId = null;
      this.saving = false;
      this.validationErrors = null;
    },
    commitUpdate() {
      this.$refs.tagObserver.validate()
        .then((valid) => {
          if (valid) {
            this.saving = true;
            this.validationErrors = null;
            const data = {
              name: this.tagName,
              noCancel: true,
            };

            if (this.update) {
              this.updateContactTag({
                organisationId: this.organisationId,
                businessId: this.businessId,
                tagId: this.tagId,
                data,
              }).then((response) => {
                if (response.status === 200) {
                  this.initialEditName = this.tagName;
                  this.showToaster('Tag has been successfully updated.', null, null, 'OK');
                  this.$emit('update', { id: this.tagId, name: this.tagName });
                  this.$refs.tagObserver.reset();
                  this.hide();
                }
              }).catch(() => {
                this.saving = false;
              });
            } else {
              this.createContactTag({
                organisationId: this.organisationId,
                businessId: this.businessId,
                data,
              }).then((response) => {
                this.initialEditName = response.name;
                this.showToaster('Tag has been successfully created.', null, null, 'OK');
                this.$emit('reload');
                this.$refs.tagObserver.reset();
                this.hide();
              }).catch(() => {
                this.saving = false;
              });
            }
          }
        });
    },
    show(tag = null) {
      this.modalTitle = 'New tag';
      if (tag) {
        this.modalTitle = `Edit ${tag.name}`;
        this.update = true;
        this.tagName = tag.name;
        this.initialEditName = tag.name;
        this.tagId = tag.id;
      }
      this.$refs.tagModal.show();
    },
    hide() {
      this.$refs.tagModal.hide();
    },
    focus() {
      if (this.$refs.nameInputRef) {
        this.$refs.nameInputRef.focus();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#tag-modal {
  .checking-input {
    position: absolute;
    top: 8px;
    right: 8px;
    left: unset;
  }
}
</style>
